import { createContext, useContext, useEffect, useState, type FC, type ReactNode } from 'react';

import { CustomEvents } from '@labradorsports/constants';
import { usePlayEditor, useSelected } from '@labradorsports/play-rendering';
import { useSite } from '@labradorsports/utils';

import { useDispatcher } from '../../shared/hooks/index.js';
import { playEditorActions } from '../../store/index.js';

const firePlayFieldResizeMobile = () => {
    if (window.innerWidth <= 576) {
        window.dispatchEvent(new Event(CustomEvents.PLAY_FIELD_RESIZE));
    }
};

/** Omit the argument (or pass a non-boolean, e.g. a click event) to toggle; pass `true` / `false` to set explicitly. */
export type PlayEditorMenuToggle = (open?: unknown) => void;

export type PlayEditorToolboxTab = 'setup' | 'movement' | 'markup' | 'concepts';

export interface PlayEditorMenuContextValue {
    showViewOptions: boolean;
    toggleViewOptions: PlayEditorMenuToggle;

    showSpotlightOptions: boolean;
    toggleSpotlightOptions: PlayEditorMenuToggle;
    updateSpotlightSelection: (spotlightPlayerIds: number[]) => void;
    activatePlayConcept: (concept: PlayConcept) => void;

    showConceptModal: boolean;
    openConceptModal: () => void;
    closeConceptModal: () => void;

    showDebugOptions: boolean;
    toggleDebug: PlayEditorMenuToggle;

    toolbarExpanded: boolean;
    toggleToolbarExpanded: PlayEditorMenuToggle;

    toolboxTab: PlayEditorToolboxTab;
    toolboxPanelOpen: boolean;
    openToolboxTab: (tab: PlayEditorToolboxTab) => void;
    closeToolboxPanel: () => void;
}

export const PlayEditorMenuContext = createContext<PlayEditorMenuContextValue>(null);

export const usePlayEditorMenu = (): PlayEditorMenuContextValue => {
    return useContext(PlayEditorMenuContext);
};

interface PlayEditorMenuProviderProps {
    children: ReactNode;
}

export const PlayEditorMenuProvider: FC<PlayEditorMenuProviderProps> = ({ children }) => {
    const updatePlay = useDispatcher(playEditorActions.UpdatePlay);
    const activatePlayConceptAction = useDispatcher(playEditorActions.ActivatePlayConcept);
    const updateDebugOptions = useDispatcher(playEditorActions.UpdateDebugOptions);
    const addingPlayers = useDispatcher(playEditorActions.SetupField);
    const { debugOptions, playData, play, setEditMode, fieldSetup, readOnly } = usePlayEditor();
    const { PlayConfig } = useSite();
    const { selected, setSelected } = useSelected();

    const [showViewOptions, setShowViewOptions] = useState(false);
    const [showSpotlightOptions, setShowSpotlightOptions] = useState(false);
    const [showConceptModal, setShowConceptModal] = useState(false);
    const [showDebugOptions, setShowDebugOptions] = useState(false);
    const [toolbarExpanded, setToolbarExpandedState] = useState(false);
    const [toolboxTab, setToolboxTab] = useState<PlayEditorToolboxTab>(() => {
        if (readOnly) return 'concepts';
        return fieldSetup ? 'setup' : 'movement';
    });
    const [toolboxPanelOpen, setToolboxPanelOpen] = useState(false);

    const conceptsOnly = Boolean(readOnly && (playData?.concepts?.length ?? 0) > 0);

    const toggleToolbarExpanded = (expanded?: unknown) => {
        const resolved = typeof expanded === 'boolean' ? expanded : !toolbarExpanded;
        setToolbarExpandedState(resolved);
        firePlayFieldResizeMobile();
    };

    const toggleDebug = (open?: unknown) => {
        const debugActive = Object.values(debugOptions ?? {}).some(Boolean);
        const resolved = typeof open === 'boolean' ? open : !debugActive;

        if (resolved) {
            setShowDebugOptions(true);
            updateDebugOptions({
                MOVEMENT_SPEED: PlayConfig.MovementSpeed,
                GRID: true,
            });
        } else {
            setShowDebugOptions(false);
            updateDebugOptions();
        }
    };

    const toggleViewOptions = (open?: unknown) => {
        const resolved = typeof open === 'boolean' ? open : !showViewOptions;

        if (resolved) {
            setSelected(null);
            setShowSpotlightOptions(false);
            setEditMode(true);
        }

        requestAnimationFrame(() => {
            firePlayFieldResizeMobile();
            setShowViewOptions(resolved);
            if (!resolved) {
                setToolbarExpandedState(false);
            }
        });
    };

    const toggleSpotlightOptions = (open?: unknown) => {
        const resolved = typeof open === 'boolean' ? open : !showSpotlightOptions;

        if (resolved) {
            setSelected(null);
            setShowViewOptions(false);
        }

        requestAnimationFrame(() => {
            firePlayFieldResizeMobile();
            setShowSpotlightOptions(resolved);
            if (!resolved) {
                setToolbarExpandedState(false);
            }
        });
    };

    const updateSpotlightSelection = (spotlightPlayerIds: number[]) => {
        updatePlay({
            viewOptions: {
                ...play.viewOptions,
                spotlightPlayerIds,
                activeConceptId: null,
            },
        });
    };

    const activatePlayConcept = (concept: PlayConcept) => {
        activatePlayConceptAction(concept);
        setShowSpotlightOptions(false);
    };

    const openConceptModal = () => setShowConceptModal(true);
    const closeConceptModal = () => setShowConceptModal(false);

    const openToolboxTab = (tab: PlayEditorToolboxTab) => {
        setToolboxTab(tab);
        setToolboxPanelOpen(true);
        addingPlayers(tab === 'setup');
    };

    const closeToolboxPanel = () => {
        setToolboxPanelOpen(false);
    };

    useEffect(() => {
        if (selected) {
            setShowViewOptions(false);
            setShowSpotlightOptions(false);
        } else {
            setToolbarExpandedState(false);
            firePlayFieldResizeMobile();
        }
    }, [selected]);

    useEffect(() => {
        if (!showViewOptions) {
            setToolbarExpandedState(false);
            firePlayFieldResizeMobile();
        }
    }, [showViewOptions]);

    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            if (!PROD && e.key === 'D' && e.ctrlKey) {
                toggleDebug();
            }
        };

        window.addEventListener('keypress', handler);

        return () => window.removeEventListener('keypress', handler);
    }, [debugOptions]);

    useEffect(() => {
        setToolbarExpandedState(false);
    }, [playData?.id]);

    useEffect(() => {
        if (conceptsOnly) {
            setToolboxTab('concepts');
        } else if (fieldSetup) {
            setToolboxTab('setup');
        } else {
            setToolboxTab((prev) => (prev === 'setup' || prev === 'concepts' ? 'movement' : prev));
        }
    }, [fieldSetup, conceptsOnly]);

    const value: PlayEditorMenuContextValue = {
        showViewOptions,
        toggleViewOptions,

        showSpotlightOptions,
        toggleSpotlightOptions,
        updateSpotlightSelection,
        activatePlayConcept,

        showConceptModal,
        openConceptModal,
        closeConceptModal,

        showDebugOptions,
        toggleDebug,

        toolbarExpanded,
        toggleToolbarExpanded,

        toolboxTab,
        toolboxPanelOpen,
        openToolboxTab,
        closeToolboxPanel,
    };

    return <PlayEditorMenuContext.Provider value={value}>{children}</PlayEditorMenuContext.Provider>;
};
